import { useNavigate, useLocation } from "react-router-dom";

/**
 * Reusable Back button for pages where the user can otherwise feel stuck
 * (Playlist detail, Club detail, Track page, etc.). Falls back to a sensible
 * default route when there is no browser history (e.g. user landed via deep
 * link or a refresh blew away the back stack).
 *
 * Usage: <BackButton fallback="/discover" label="Back" />
 *
 * Phase 19.1.1 — Reliability fix #2: production users reported the button
 * still "did nothing" on Crew / Group / Club pages even after the
 * `document.referrer` → `location.key` switch. Root cause: `navigate(-1)`
 * can no-op or land on an unexpected entry (auth redirect, replaced history,
 * cross-origin referrer) and there is no way for us to detect that from
 * client code.
 *
 * Fix: pages can opt into `alwaysFallback`, which makes the button ALWAYS
 * navigate to the supplied fallback route. This is the contract crews,
 * groups and clubs are documented to honour ("Crew → /crews", etc.) and
 * removes the entire class of "stuck on detail page" production bugs.
 *
 * Pages that still want true browser back leave `alwaysFallback` undefined.
 */
const BackButton = ({
  fallback = "/discover",
  label = "Back",
  className = "",
  testId = "back-button",
  alwaysFallback = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const handle = () => {
    // Pages that want a deterministic landing spot skip the history dance.
    if (alwaysFallback) {
      navigate(fallback);
      return;
    }
    const hasSpaHistory = location.key && location.key !== "default";
    if (hasSpaHistory) {
      navigate(-1);
    } else {
      navigate(fallback);
    }
  };
  return (
    <button
      type="button"
      onClick={handle}
      data-testid={testId}
      // Phase 19.1.1 fix #2 — `relative z-[130]` lifts the click target above
      // the StripeHealthBanner (z-[120]) and SharedNavigation (z-[55]).
      // In production those banners were intercepting back-button clicks at
      // the top of detail pages, which is why users reported "back does
      // nothing" even after the location.key fix.
      className={`relative z-[130] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-gray-800/70 hover:bg-gray-700 text-gray-200 border border-gray-700 transition-colors ${className}`}
    >
      <span aria-hidden>←</span>
      <span>{label}</span>
    </button>
  );
};

export default BackButton;
